import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/auth';
import { apiClient } from '@/api/client';
import { teamApi, inviteApi, authApi } from '@/api/auth';
import type { TeamMember, InviteRecord } from '@/api/auth';

const ROLES = ['admin', 'analyst', 'viewer'] as const;

export function TeamManagement() {
  const { user, getEffectiveRole } = useAuthStore();
  const effectiveRole = getEffectiveRole();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [seats, setSeats] = useState<number | null>(null);  // seats_purchased
  const [orgProducts, setOrgProducts] = useState<string[]>([]);  // products the org actually owns
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('analyst');
  const [inviteProducts, setInviteProducts] = useState<string[]>([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Confirm deactivation
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [teamData, inviteData, lic, prods] = await Promise.all([
        teamApi.list(),
        inviteApi.list(),
        apiClient.get('/phreatic/billing/license').then(r => r.data).catch(() => null),
        apiClient.get('/clients/current/products').then(r => r.data).catch(() => []),
      ]);
      setMembers(teamData);
      setInvites(inviteData);
      if (lic) setSeats(lic.seats_purchased ?? null);
      // Only products the org actually owns are toggleable per member.
      setOrgProducts(
        (Array.isArray(prods) ? prods : [])
          .filter((p: any) => String(p.status).toLowerCase() === 'active')
          .map((p: any) => p.product_slug)
      );
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error?.response?.data?.detail || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await teamApi.updateRole(userId, newRole);
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, role: newRole } : m));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      alert(error?.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleProductToggle = async (userId: string, currentProducts: string[], product: string) => {
    const newProducts = currentProducts.includes(product)
      ? currentProducts.filter(p => p !== product)
      : [...currentProducts, product];
    try {
      await teamApi.updateProducts(userId, newProducts);
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, products: newProducts } : m));

      // If the updated user is the current user, refresh the auth store immediately
      if (user && userId === user.id) {
        const me = await authApi.getMe();
        useAuthStore.getState().setProducts(me.products || []);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      alert(error?.response?.data?.detail || 'Failed to update products');
    }
  };

  const handleAllocation = async (userId: string, raw: string) => {
    const tokens = raw.trim() === '' ? null : Math.max(0, parseInt(raw, 10) || 0);
    try {
      await teamApi.updateAllocation(userId, tokens);
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, token_allocation: tokens } : m));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      alert(error?.response?.data?.detail || 'Failed to update allocation');
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await teamApi.deactivate(userId);
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, is_active: false } : m));
      setConfirmDeactivate(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      alert(error?.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleInvite = async () => {
    setInviteError('');
    setInviteLoading(true);
    try {
      await inviteApi.create(inviteEmail, inviteRole, inviteProducts);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('analyst');
      setInviteProducts([]);
      fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setInviteError(error?.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevokeInvite = async (id: string) => {
    try {
      await inviteApi.revoke(id);
      setInvites(prev => prev.map(i => i.id === id ? { ...i, status: 'revoked' } : i));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      alert(error?.response?.data?.detail || 'Failed to revoke invitation');
    }
  };

  if (effectiveRole !== 'admin' && effectiveRole !== 'superadmin') {
    return (
      <div className="text-center py-20">
        <p className="text-mono-400 text-sm tracking-widest">ADMIN ACCESS REQUIRED</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-20">
        <p className="text-mono-400 text-sm tracking-widest animate-pulse">LOADING TEAM...</p>
      </div>
    );
  }

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const pastInvites = invites.filter(i => i.status !== 'pending');

  // Seats: active members + pending invites count against seats_purchased
  // (matches the backend invite cap).
  const activeMembers = members.filter(m => m.is_active !== false).length;
  const seatsUsed = activeMembers + pendingInvites.length;
  const seatsAvailable = seats != null ? Math.max(0, seats - seatsUsed) : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-mono-100 tracking-widest">TEAM MANAGEMENT</h1>
          <p className="text-xs text-mono-500 tracking-wider mt-1">
            {members.length} MEMBER{members.length !== 1 ? 'S' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs tracking-widest font-medium rounded transition-colors"
        >
          INVITE TEAM MEMBER
        </button>
      </div>

      {/* Seats summary */}
      {seats != null && (
        <div className="flex items-center gap-8 border border-white/10 bg-white/[0.05] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-lg p-4 flex-wrap">
          <div>
            <div className="text-[10px] text-mono-500 uppercase tracking-wider">Seats Paid</div>
            <div className="text-lg font-semibold text-mono-100">{seats}</div>
          </div>
          <div>
            <div className="text-[10px] text-mono-500 uppercase tracking-wider">Used</div>
            <div className="text-lg font-semibold text-mono-100">{seatsUsed}</div>
            <div className="text-[10px] text-mono-600">{activeMembers} members · {pendingInvites.length} pending</div>
          </div>
          <div>
            <div className="text-[10px] text-mono-500 uppercase tracking-wider">Available</div>
            <div className={`text-lg font-semibold ${seatsAvailable === 0 ? 'text-amber-400' : 'text-cyan-400'}`}>
              {seatsAvailable}
            </div>
          </div>
          {seatsAvailable === 0 && (
            <div className="text-[10px] text-amber-400 self-center">
              No seats left — buy more in Billing to invite more members.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="border border-red-500/30 bg-red-500/10 p-3 rounded">
          <p className="text-red-400 text-xs tracking-wider">{error}</p>
        </div>
      )}

      {/* Team Members Table */}
      <div className="border border-white/10 bg-white/[0.04] backdrop-blur-md rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-white/[0.04] border-b border-white/10">
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">NAME</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">EMAIL</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">ROLE</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">PRODUCTS</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">TOKENS (USED / CAP)</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">STATUS</th>
              <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">LAST LOGIN</th>
              <th className="text-right text-[10px] text-mono-500 tracking-widest p-3">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id} className="border-b border-mono-800/50 hover:bg-mono-900/50">
                <td className="p-3">
                  <span className="text-xs text-mono-200">{member.full_name}</span>
                </td>
                <td className="p-3">
                  <span className="text-xs text-mono-400">{member.email}</span>
                </td>
                <td className="p-3">
                  <select
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                    disabled={member.id === user?.id}
                    className="bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-2 py-1 tracking-wider disabled:opacity-50"
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r.toUpperCase()}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {orgProducts.length === 0 && (
                      <span className="text-[9px] text-mono-600 tracking-wider">NO PRODUCTS</span>
                    )}
                    {orgProducts.map(product => (
                      <button
                        key={product}
                        onClick={() => handleProductToggle(member.id, member.products, product)}
                        className={`text-[9px] px-1.5 py-0.5 rounded tracking-wider transition-colors ${
                          member.products.includes(product)
                            ? 'bg-cyan-900/50 text-cyan-300 border border-cyan-700/50'
                            : 'bg-mono-800 text-mono-500 border border-mono-700/50 hover:text-mono-300'
                        }`}
                      >
                        {product.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-mono-400 tabular-nums">{member.token_used.toLocaleString()}</span>
                    <span className="text-mono-600">/</span>
                    <input
                      type="number"
                      min={0}
                      defaultValue={member.token_allocation ?? ''}
                      placeholder="∞"
                      onBlur={(e) => handleAllocation(member.id, e.target.value)}
                      title="Per-seat token cap — leave blank for unlimited (draws from the shared org pool)"
                      className="w-20 bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-2 py-1 tracking-wider placeholder-mono-600 focus:outline-none focus:border-cyan-600"
                    />
                  </div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] tracking-wider ${member.is_active ? 'text-emerald-400' : 'text-red-400'}`}>
                    {member.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-[10px] text-mono-500 tracking-wider">
                    {member.last_login
                      ? new Date(member.last_login).toLocaleDateString()
                      : 'NEVER'}
                  </span>
                </td>
                <td className="p-3 text-right">
                  {member.id !== user?.id && member.is_active && (
                    <>
                      {confirmDeactivate === member.id ? (
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-[10px] text-red-400 tracking-wider">CONFIRM?</span>
                          <button
                            onClick={() => handleDeactivate(member.id)}
                            className="text-[10px] text-red-400 hover:text-red-300 tracking-wider"
                          >
                            YES
                          </button>
                          <button
                            onClick={() => setConfirmDeactivate(null)}
                            className="text-[10px] text-mono-500 hover:text-mono-300 tracking-wider"
                          >
                            NO
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeactivate(member.id)}
                          className="text-[10px] text-red-500/60 hover:text-red-400 tracking-wider"
                        >
                          REMOVE
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div>
          <h2 className="text-xs font-bold text-mono-300 tracking-widest mb-3">PENDING INVITATIONS</h2>
          <div className="border border-white/10 bg-white/[0.04] backdrop-blur-md rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/[0.04] border-b border-white/10">
                  <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">EMAIL</th>
                  <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">ROLE</th>
                  <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">PRODUCTS</th>
                  <th className="text-left text-[10px] text-mono-500 tracking-widest p-3">EXPIRES</th>
                  <th className="text-right text-[10px] text-mono-500 tracking-widest p-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map(invite => (
                  <tr key={invite.id} className="border-b border-mono-800/50">
                    <td className="p-3 text-xs text-mono-300">{invite.email}</td>
                    <td className="p-3 text-xs text-mono-400 tracking-wider">{invite.role.toUpperCase()}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(invite.products || []).map(p => (
                          <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-900/30 text-cyan-400 tracking-wider">
                            {p.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-[10px] text-mono-500 tracking-wider">
                      {new Date(invite.expires_at).toLocaleDateString()}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="text-[10px] text-red-500/60 hover:text-red-400 tracking-wider"
                      >
                        REVOKE
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-sm font-bold text-mono-100 tracking-widest mb-4">INVITE TEAM MEMBER</h2>

            {inviteError && (
              <div className="border border-red-500/30 bg-red-500/10 p-2 rounded mb-4">
                <p className="text-red-400 text-xs tracking-wider">{inviteError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] text-mono-500 tracking-widest mb-1">EMAIL</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-3 py-2 tracking-wider placeholder-mono-600 focus:outline-none focus:border-cyan-600"
                />
              </div>

              <div>
                <label className="block text-[10px] text-mono-500 tracking-widest mb-1">ROLE</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full bg-mono-800 border border-mono-700 text-mono-200 text-xs rounded px-3 py-2 tracking-wider focus:outline-none focus:border-cyan-600"
                >
                  <option value="analyst">ANALYST</option>
                  <option value="viewer">VIEWER</option>
                  <option value="admin">ADMIN</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] text-mono-500 tracking-widest mb-1">PRODUCTS</label>
                <div className="flex flex-wrap gap-1.5">
                  {orgProducts.map(product => (
                    <button
                      key={product}
                      type="button"
                      onClick={() => {
                        setInviteProducts(prev =>
                          prev.includes(product)
                            ? prev.filter(p => p !== product)
                            : [...prev, product]
                        );
                      }}
                      className={`text-[10px] px-2 py-1 rounded tracking-wider transition-colors ${
                        inviteProducts.includes(product)
                          ? 'bg-cyan-900/50 text-cyan-300 border border-cyan-700/50'
                          : 'bg-mono-800 text-mono-500 border border-mono-700/50 hover:text-mono-300'
                      }`}
                    >
                      {product.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteError('');
                }}
                className="px-4 py-2 text-xs text-mono-400 hover:text-mono-200 tracking-widest"
              >
                CANCEL
              </button>
              <button
                onClick={handleInvite}
                disabled={!inviteEmail || inviteLoading}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs tracking-widest font-medium rounded transition-colors"
              >
                {inviteLoading ? 'SENDING...' : 'SEND INVITE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
